import { useEffect, useState } from "react";
import { Drawer } from "antd";
import { Link, useParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import { publicAsset } from "./careData";

const residentSections = [
  ["notes", "ti-notes", "Daily Notes", "Behavior, health, welfare, care updates, and discrepancies.", "18 notes", "2024-05-24T09:15:00"],
  ["carePlan", "ti-heart-handshake", "Negotiated Care Plans", "Preliminary plans, 30-day observations, final NCPs, and state confirmations.", "In review", "2024-05-24T08:30:00"],
  ["documents", "ti-file-description", "Documents", "Signed agreements, policies, PoA documents, and state files.", "8 files", "2024-05-23T16:10:00"],
  ["alerts", "ti-alert-triangle", "Incidents & Alerts", "Open incidents, care risks, and follow-up items.", "2 open", "2024-05-23T11:20:00"],
  ["assessments", "ti-clipboard-text", "Assessments", "State assessments, annual reviews, and assessment changes.", "2 pending", "2024-05-22T14:00:00"],
  ["team", "ti-users-group", "Care Teams", "PCP, therapy, PoA/family, social worker, and internal staff.", "5 members", "2024-05-21T10:00:00"],
].sort((a, b) => new Date(b[5]).getTime() - new Date(a[5]).getTime());

const residentTabs = [
  ["overview", "Overview"],
  ["onboarding-bio", "Bio Data"],
  ["onboarding-contacts", "Contacts"],
  ["onboarding-care-history", "Care History"],
  ["onboarding-care-snapshot", "Care Snapshot"],
  ["onboarding-documents", "Onboarding Documents"],
  ["assessment", "Assessments"],
  ["preliminary-plan", "Preliminary Plans"],
  ["negotiated-care-plan", "Negotiated Care Plans"],
  ["daily-notes", "Daily Notes"],
  ["documents", "Documents"],
  ["care-team", "Care Teams"],
];

const residentFlowType = "new-resident";

const carePlanFlowStages = [
  {
    label: residentFlowType === "new-resident" ? "New Resident Onboarding" : "Current Resident New Year Start",
    text: residentFlowType === "new-resident" ? "Admission flow started" : "Annual review flow started",
    status: "Complete",
    icon: "ti-flag",
    appliesTo: ["new-resident", "existing-resident"],
  },
  {
    label: "Assessment",
    text: "State assessment received",
    status: "Complete",
    icon: "ti-clipboard-text",
    appliesTo: ["new-resident", "existing-resident"],
  },
  {
    label: "Preliminary Plan",
    text: "Provider admission plan",
    status: "Complete",
    icon: "ti-heart-handshake",
    appliesTo: ["new-resident"],
  },
  {
    label: "Daily Notes / 30 Days",
    text: "Observation period complete",
    status: "Complete",
    icon: "ti-notes",
    appliesTo: ["new-resident"],
  },
  {
    label: "NCP",
    text: "Negotiated plan in review",
    status: "Current",
    icon: "ti-file-pencil",
    appliesTo: ["new-resident", "existing-resident"],
  },
  {
    label: "Confirmed NCP",
    text: "State confirmation pending",
    status: "Pending",
    icon: "ti-circle-check",
    appliesTo: ["new-resident", "existing-resident"],
  },
];

const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const compactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const ageFromBirthDate = (birthDate) => {
  if (!birthDate) return "";
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());
  if (!hasBirthdayPassed) age -= 1;
  return age > -1 ? String(age) : "";
};
const fullNameFor = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Resident";
const locationFor = (resident = {}) => resident.locationInformation || resident.address || resident.location || {};
const contactFor = (resident = {}) => resident.contactInformation || resident.contact || {};
const addressSummaryFor = (resident = {}) => {
  const location = locationFor(resident);
  return [
    location.addressLine1 || location.address,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.country,
    location.zipCode || location.zip,
  ].filter(Boolean).join(", ");
};
const primaryPhoneFor = (resident = {}) => {
  const contact = contactFor(resident);
  return resident.primaryContactPhone || contact.primaryPhone || resident.phone || "";
};
const primaryEmailFor = (resident = {}) => {
  const contact = contactFor(resident);
  return resident.primaryContactEmail || contact.primaryEmail || resident.email || "";
};
const displayValue = (value, fallback = "Not set") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || fallback;
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};
const labelize = (value) => {
  const text = displayValue(value, "");
  return text ? text.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()) : "";
};
const hasHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
const onboardingValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.filter(Boolean).map(onboardingValue).join(", ");
  if (typeof value === "object") return Object.entries(value)
    .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== "")
    .map(([key, entryValue]) => `${labelize(key)}: ${onboardingValue(entryValue)}`)
    .join(" | ");
  return String(value);
};
const addressSummary = (address = {}) => [
  address.addressLine1 || address.address,
  address.addressLine2,
  address.city,
  address.county,
  address.state,
  address.country,
  address.zipCode || address.zip,
].filter(Boolean).join(", ");
const phonesSummary = (phones = {}) => [
  phones.preferred ? `Preferred: ${phones.preferred}` : "",
  phones.mobile ? `Mobile: ${phones.mobile}` : "",
  phones.office ? `Office: ${phones.office}` : "",
  phones.home ? `Home: ${phones.home}` : "",
].filter(Boolean).join(" | ");

const assignedStaffToday = [
  {
    key: "staff-sarah-mitchell",
    name: "Sarah Mitchell",
    role: "Lead caregiver",
    shift: "7:00 AM - 3:00 PM",
    phone: "(206) 555-0173",
    email: "sarah.mitchell@olreadia.care",
    assignment: "Morning medications, mobility support, daily notes",
  },
  {
    key: "staff-daniel-reed",
    name: "Daniel Reed",
    role: "Care aide",
    shift: "3:00 PM - 11:00 PM",
    phone: "(206) 555-0197",
    email: "daniel.reed@olreadia.care",
    assignment: "Evening care, meals, safety rounds",
  },
];

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const todayDate = dateInputValue(new Date());
const yesterdayDate = dateInputValue(shiftDate(todayDate, -1));
const olderNoteDate = dateInputValue(shiftDate(todayDate, -4));

const negotiatedPlanSources = [
  ["Preliminary Plan", "Admission preliminary plan", "May 7, 2024", "Included", "ti-heart-handshake"],
  ["State Assessment", "Initial state assessment packet", "May 6, 2024", "Included", "ti-clipboard-text"],
  ["Daily Notes", "30-day observation notes and discrepancies", "May 20, 2024", "Included", "ti-notes"],
  ["MaG", "Meet-and-Greet hospital evaluation", "May 6, 2024", "Included", "ti-user-check"],
  ["Family Inputs", "PoA and family care preferences", "May 7, 2024", "Included", "ti-users-group"],
  ["Care Team Updates", "PCP, PT, social worker, and internal staff updates", "May 24, 2024", "Pending review", "ti-stethoscope"],
];

const drawerContent = {
  ...Object.fromEntries(assignedStaffToday.map((staff) => [
    staff.key,
    {
      title: staff.name,
      intro: `${staff.role} assigned to Margaret Johnson today.`,
      sections: [
        {
          title: "Staff Details",
          rows: [
            ["Name", staff.name],
            ["Role", staff.role],
            ["Shift", staff.shift],
            ["Assignment", staff.assignment],
          ],
        },
        {
          title: "Contact Information",
          rows: [
            ["Phone", staff.phone],
            ["Email", staff.email],
          ],
        },
      ],
      contactActions: [
        ["Chat", "ti-message-circle", "#chat"],
        ["Call", "ti-phone", `tel:${staff.phone.replace(/[^+\d]/g, "")}`],
      ],
      action: "View schedule",
    },
  ])),
  persona: {
    title: "Resident Persona",
    intro: "Resident bio data, contact information, and next of kin details.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", "Margaret Johnson"],
          ["Date of birth", "August 14, 1941"],
          ["Age", "82"],
          ["Gender", "Female"],
          ["Room", "214A"],
          ["Admission date", "May 6, 2024"],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", "(206) 555-0184"],
          ["Email", "margaret.johnson@example.com"],
          ["Address", "4821 Cedar Lane, Seattle, WA 98112"],
          ["Preferred contact", "Phone through PoA"],
        ],
      },
      {
        title: "Next of Kin",
        rows: [
          ["Name", "Elaine Johnson"],
          ["Relationship", "Daughter / PoA"],
          ["Phone", "(206) 555-0128"],
          ["Email", "elaine.johnson@example.com"],
        ],
      },
    ],
    action: "Edit persona",
  },
  notes: {
    title: "Daily Notes",
    intro: "Capture daily behavior, health, welfare, discrepancies, and changes for Margaret Johnson.",
    rows: [
      ["Today at 9:15 AM", "Mood stable after breakfast. Mild shortness of breath noted during hallway walk."],
      ["Yesterday at 4:30 PM", "Family shared that door should remain slightly open during sleep."],
      ["May 12 at 11:20 AM", "Transfer assistance updated from independent to one-person assist."],
    ],
    action: "Add daily note",
  },
  documents: {
    title: "Files and Documents",
    intro: "Review signed paperwork, state assessment files, PoA documents, and confirmed care plan files.",
    rows: [
      ["State Initial Assessment.pdf", "Uploaded May 6, 2024"],
      ["Admission Agreements.pdf", "Signed May 7, 2024"],
      ["PoA Documentation.pdf", "Verified May 7, 2024"],
      ["Negotiated Care Plan Draft.pdf", "Updated May 24, 2024"],
    ],
    action: "Upload file",
  },
  assessments: {
    title: "Assessments",
    intro: "Review state assessments, annual assessment changes, and assessment items that affect care planning.",
    rows: [
      ["Initial state assessment", "Medication behavior, diagnosis, history, and care needs imported."],
      ["MaG evaluation", "Provider review notes show transfer discrepancy."],
      ["Final assessment update", "Pending final provider input before state confirmation."],
    ],
    action: "Add new assessment",
  },
  assessmentCreate: {
    title: "New Assessment",
    intro: "Add a state assessment record, attach the source files, and capture the care planning details that should feed the resident record.",
    form: "assessment",
  },
  carePlan: {
    title: "Negotiated Care Plans",
    intro: "Manage preliminary plans, 30-day observations, provider inputs, pay negotiation, and state confirmation.",
    rows: [
      ["Preliminary care plan", "Created as soon as resident arrived home."],
      ["30-day probation", "18 notes captured; discrepancies documented."],
      ["Final NCP", "Ready for provider review and social worker negotiation."],
    ],
    action: "Open NCP workspace",
  },
  team: {
    title: "Care Teams",
    intro: "Coordinate updates with PCP, therapy, social worker, PoA/family, and internal care staff.",
    rows: [
      ["PCP", "Dr. Helen Morris"],
      ["Physical Therapist", "Jason Reed"],
      ["PoA / Family", "Elaine Johnson"],
      ["Social Worker", "Amanda Parker"],
    ],
    action: "Message care team",
  },
  alerts: {
    title: "Incidents and Alerts",
    intro: "Track incidents, health or welfare risks, and follow-up items.",
    rows: [
      ["Fall risk", "High priority; hallway walk supervision required."],
      ["Transfer discrepancy", "State assessment conflicts with MaG/home observation."],
    ],
    action: "Add alert",
  },
};

const tabItems = {
  assessment: {
    createLabel: "Start assessment process",
    drawerKey: "assessments",
    createDrawerKey: "assessmentCreate",
    currentLabel: "Current assessment process",
    gridTemplate: "minmax(180px, 1fr) 105px 120px 120px 115px minmax(190px, 1.1fr) 135px 28px",
    columns: [
      ["Assessment Process", "processName"],
      ["Patient Type", "patientType", "status"],
      ["Start Date", "startDate", "date"],
      ["End Date", "endDate", "date"],
      ["Status", "status", "status"],
      ["Attached Records", "attachedRecords", "attachedRecords"],
      ["Social Worker", "socialWorker"],
    ],
    current: {
      processName: "New patient assessment process",
      summary: "New resident onboarding from state initial assessment through residency confirmation.",
      patientType: "NEW",
      startDate: "2024-05-06T10:30:00",
      endDate: "2024-06-05T17:00:00",
      status: "In progress",
      dateAdded: "2024-05-06T10:30:00",
      attachedRecords: ["Initial Assessment", "Meet and Greet", "Client Onboarding", "Resident 30-Day Probation", "Preliminary Plan", "Notes", "Negotiated Plan"],
      socialWorker: "Amanda Parker",
      addedBy: "Sarah Mitchell",
    },
    rows: [
      {
        processName: "Resident annual assessment process 2023",
        summary: "Existing resident yearly review and updated negotiated plan.",
        patientType: "RESIDENT",
        startDate: "2023-06-12T14:10:00",
        endDate: "2023-07-10T15:00:00",
        status: "Confirmed",
        dateAdded: "2023-06-12T14:10:00",
        attachedRecords: ["Initial Assessment Review", "Notes", "Negotiated Plan", "Final Assessment", "Residency Confirmation"],
        socialWorker: "Amanda Parker",
        addedBy: "Amanda Parker",
      },
      {
        processName: "Resident change review",
        summary: "Change-of-condition assessment process for updated support needs.",
        patientType: "RESIDENT",
        startDate: "2023-02-03T09:45:00",
        endDate: "2023-02-21T13:30:00",
        status: "Closed",
        dateAdded: "2023-02-03T09:45:00",
        attachedRecords: ["Initial Assessment Review", "Notes", "Negotiated Plan", "Final Assessment"],
        socialWorker: "Martin Lee",
        addedBy: "Sarah Mitchell",
      },
    ],
  },
  "preliminary-plan": {
    createLabel: "Create preliminary plan",
    drawerKey: "carePlan",
    currentLabel: "Current preliminary plan",
    gridTemplate: "minmax(190px, 1fr) 130px 150px 120px 28px",
    columns: [
      ["Plan", "title"],
      ["Created", "dateAdded", "date"],
      ["Prepared By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: {
      title: "Admission preliminary plan",
      summary: "Provider-created plan after resident arrived home.",
      dateAdded: "2024-05-07T09:00:00",
      addedBy: "Sarah Mitchell",
      status: "Active",
    },
    rows: [
      {
        title: "MaG care assumptions",
        summary: "Initial support assumptions from hospital visit.",
        dateAdded: "2024-05-06T16:45:00",
        addedBy: "Sarah Mitchell",
        status: "Superseded",
      },
    ],
  },
  "negotiated-care-plan": {
    createLabel: "Create negotiated plan",
    drawerKey: "carePlan",
    currentLabel: "Current negotiated care plan",
    gridTemplate: "minmax(200px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["Plan", "title"],
      ["Created", "dateAdded", "date"],
      ["Social Worker", "socialWorker"],
      ["Status", "status", "status"],
    ],
    current: {
      title: "Final NCP draft",
      summary: "Generated from 30-day notes and provider inputs.",
      dateAdded: "2024-05-24T08:30:00",
      socialWorker: "Amanda Parker",
      status: "In review",
    },
    rows: [
      {
        title: "30-day observation plan",
        summary: "Discrepancies and care changes collected.",
        dateAdded: "2024-05-20T11:00:00",
        socialWorker: "Amanda Parker",
        status: "Working",
      },
      {
        title: "Preliminary care plan",
        summary: "Created from assessment, MaG, paperwork, and family discovery.",
        dateAdded: "2024-05-07T09:00:00",
        socialWorker: "Amanda Parker",
        status: "Complete",
      },
    ],
  },
  "daily-notes": {
    createLabel: "Add daily note",
    drawerKey: "notes",
    currentLabel: "Latest daily note",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["Note", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Added By", "addedBy"],
      ["Category", "category", "status"],
    ],
    current: {
      title: "Mood and walk observation",
      summary: "Mood stable after breakfast; mild shortness of breath during hallway walk.",
      dateAdded: `${todayDate}T09:15:00`,
      addedBy: "Sarah Mitchell",
      category: "Health",
    },
    rows: [
      {
        title: "Family sleep preference",
        summary: "Door slightly open and hallway light visible improves sleep.",
        dateAdded: `${yesterdayDate}T16:30:00`,
        addedBy: "Sarah Mitchell",
        category: "Welfare",
      },
      {
        title: "Transfer support update",
        summary: "One-person assist needed despite independent transfer assessment.",
        dateAdded: `${olderNoteDate}T11:20:00`,
        addedBy: "Sarah Mitchell",
        category: "Discrepancy",
      },
    ],
  },
  documents: {
    createLabel: "Upload document",
    drawerKey: "documents",
    currentLabel: "Current document",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["File", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Uploaded By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: {
      title: "Negotiated Care Plan Draft.pdf",
      summary: "Updated care plan draft.",
      dateAdded: "2024-05-24T08:30:00",
      addedBy: "Sarah Mitchell",
      status: "Draft",
    },
    rows: [
      {
        title: "PoA Documentation.pdf",
        summary: "Verified decision-maker file.",
        dateAdded: "2024-05-07T14:00:00",
        addedBy: "Amanda Parker",
        status: "Verified",
      },
      {
        title: "Admission Agreements.pdf",
        summary: "Signed policies and agreements.",
        dateAdded: "2024-05-07T11:30:00",
        addedBy: "Sarah Mitchell",
        status: "Signed",
      },
      {
        title: "State Initial Assessment.pdf",
        summary: "State assessment packet.",
        dateAdded: "2024-05-06T10:30:00",
        addedBy: "Sarah Mitchell",
        status: "Imported",
      },
    ],
  },
  "care-team": {
    createLabel: "Add care team member",
    drawerKey: "team",
    currentLabel: "Primary care team member",
    gridTemplate: "minmax(190px, 1fr) 130px 145px 140px 28px",
    columns: [
      ["Team Member", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Added By", "addedBy"],
      ["Role", "role", "status"],
    ],
    current: {
      title: "Amanda Parker",
      summary: "Social worker assigned for NCP negotiation.",
      dateAdded: "2024-05-24T10:00:00",
      addedBy: "Sarah Mitchell",
      role: "Social Worker",
    },
    rows: [
      {
        title: "Jason Reed",
        summary: "Physical therapy added for mobility support.",
        dateAdded: "2024-05-14T09:30:00",
        addedBy: "Sarah Mitchell",
        role: "PT",
      },
      {
        title: "Elaine Johnson",
        summary: "PoA / family contact confirmed.",
        dateAdded: "2024-05-07T13:00:00",
        addedBy: "Sarah Mitchell",
        role: "PoA",
      },
      {
        title: "Dr. Helen Morris",
        summary: "Primary care provider.",
        dateAdded: "2024-05-06T12:00:00",
        addedBy: "Amanda Parker",
        role: "PCP",
      },
    ],
  },
};

const sortedRecordRows = (rows) =>
  [...rows].sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime());

const recordTitle = (record) => record.period || record.title || record.name || record.file;

const recordSummary = (record) => record.summary || record.socialWorker || record.addedBy || record.status || record.role || record.category;

const recordBadge = (record) => record.status || record.role || record.category || "Current";

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const ncpFormSections = [
  {
    title: "Plan Header",
    fields: [
      ["Provider's Name", "providerName", "text"],
      ["Date NCP Started", "ncpStarted", "date"],
      ["Moved In Date", "movedInDate", "date"],
      ["Date Completed", "dateCompleted", "date"],
      ["Date Discharged", "dateDischarged", "date"],
    ],
  },
  {
    title: "Resident Information",
    fields: [
      ["Resident's Name", "residentName", "text"],
      ["Pronouns", "pronouns", "text"],
      ["Date of Birth / Age", "dobAge", "text"],
      ["Primary Language", "primaryLanguage", "text"],
      ["Speaks English?", "speaksEnglish", "select", ["Yes", "No"]],
      ["Interpreter Needed?", "interpreterNeeded", "select", ["No", "Yes"]],
      ["Allergies", "allergies", "textarea"],
    ],
  },
  {
    title: "Legal, Specialty, and Evacuation",
    fields: [
      ["Legal Documents", "legalDocuments", "checkboxes", ["None", "Advanced Directives", "POLST Form", "Other"]],
      ["Specialty Needs", "specialtyNeeds", "checkboxes", ["No", "Dementia", "Mental Health", "Developmental Disability", "Other"]],
      ["Evacuation Assistance Required", "evacuationAssistance", "select", ["None - resident is independent", "Assistance required"]],
      ["Resident Evacuation and Safety Instructions", "evacuationInstructions", "textarea"],
      ["Mental/Physical Health - Current Medical Status/Diagnosis", "medicalStatus", "textarea"],
    ],
  },
  {
    title: "Responsible Parties - Contacts",
    fields: [
      ["Case Manager", "caseManager", "text"],
      ["DPOA / Guardian", "guardian", "text"],
      ["Medical or Mental Health Provider", "healthProvider", "text"],
      ["Pharmacy", "pharmacy", "text"],
      ["Preferred Contact Method", "preferredContact", "select", ["Phone", "Cell", "Email", "Fax"]],
      ["Contact Notes", "contactNotes", "textarea"],
    ],
  },
  {
    title: "Communication",
    fields: [
      ["Modes of Expression", "modesOfExpression", "textarea"],
      ["Problems with Mode of Expression?", "expressionProblems", "select", ["No", "Yes"]],
      ["Speech/Hearing/Vision Equipment", "communicationEquipment", "textarea"],
      ["How resident makes self-understood", "selfUnderstood", "textarea"],
      ["How resident understands others", "understandsOthers", "textarea"],
      ["Ability to Use Phone", "phoneAbility", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Communication Caregiver Instructions", "communicationInstructions", "textarea"],
    ],
  },
  {
    title: "Medication Management",
    fields: [
      ["Medication Overview", "medicationOverview", "checkboxes", ["Medication allergies", "More than one medication assistance need", "Psychopharmacologic medications", "See MAR"]],
      ["Meds Ordered By", "medsOrderedBy", "text"],
      ["Meds Delivered By", "medsDeliveredBy", "text"],
      ["Medication Assistance Type", "medicationAssistanceType", "select", ["Self-administration", "Self-administration with assistance", "Medication administration"]],
      ["Medication Types", "medicationTypes", "checkboxes", ["Oral", "Topical", "Eye drops/ointment", "Inhalers", "Sprays", "Injections", "Allergy Kits", "Suppositories", "Other"]],
      ["Nurse Delegation / RN Delegator", "nurseDelegation", "textarea"],
      ["Medication Strengths and Abilities", "medicationStrengths", "textarea"],
      ["Medication Assistance Required / Caregiver Will", "medicationCaregiverInstructions", "textarea"],
      ["Medication Refusal Plan", "medicationRefusalPlan", "textarea"],
    ],
  },
  {
    title: "Health, Treatments, and Therapies",
    fields: [
      ["Health Indicators", "healthIndicators", "checkboxes", ["Pain", "Weight loss/gain", "Vital signs", "Hospitalization or ER visits", "Other"]],
      ["Pain Impact / Current Weight / Height / Notes", "healthIndicatorNotes", "textarea"],
      ["Treatments / Programs / Therapies", "treatments", "checkboxes", ["Oxygen Use", "Dialysis", "Blood Thinners", "INR/LAB", "Blood Glucose Monitoring", "Injection", "CPAP/BIPAP", "Nebulizer", "PT/OT/ST", "Home Health", "Adult Day Health", "Hospice", "Meaningful Day"]],
      ["Treatment Provider / Vendor / Agency Details", "treatmentDetails", "textarea"],
      ["Treatment Assistance Required / Caregiver Will", "treatmentAssistance", "textarea"],
      ["Treatment Refusal Plan", "treatmentRefusalPlan", "textarea"],
    ],
  },
  {
    title: "Psych/Social/Cognitive Status",
    fields: [
      ["Sleep and Nighttime Support", "sleepSupport", "textarea"],
      ["Memory Impairment", "memoryImpairment", "checkboxes", ["Short-term", "Long-term", "Oriented to person"]],
      ["Behaviors", "behaviors", "checkboxes", ["Impaired decision making", "Disruptive behavior", "Assaultive", "Resistive to care", "Depression", "Anxiety", "Irritability", "Disorientation", "Wandering/Pacing", "Exit seeking", "Hallucinations", "Delusions", "Unsafe behavior", "Other"]],
      ["Behavior Support / Crisis Plan", "behaviorSupport", "textarea"],
      ["Mental Health Provider / Program", "mentalHealthProvider", "textarea"],
      ["Typical Day Narrative", "typicalDay", "textarea"],
      ["Left Alone Plan", "leftAlonePlan", "textarea"],
      ["Universal Precautions", "universalPrecautions", "textarea"],
    ],
  },
  {
    title: "Activities of Daily Living",
    fields: [
      ["Functional Limitations Impacting ADLs", "adlLimitations", "textarea"],
      ["Ambulation / Mobility", "ambulation", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Bed Mobility / Transfer", "transfer", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Eating", "eating", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Toileting / Continence", "toileting", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Dressing", "dressing", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Personal Hygiene", "personalHygiene", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Bathing", "bathing", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Foot Care", "footCare", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Skin Care", "skinCare", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["ADL Caregiver Instructions", "adlInstructions", "textarea"],
    ],
  },
  {
    title: "Instrumental ADLs and Activities",
    fields: [
      ["Managing Finances", "finances", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Shopping", "shopping", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Transportation", "transportation", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Activities / Social", "activitiesSocial", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Activity Preferences", "activityPreferences", "checkboxes", ["Reading", "Phone conversation/visiting", "Bible study or church", "Visitors", "Gardening", "Outings", "Exercises/range of motion", "Cooking or baking", "Watching TV/movies", "Arts and crafts", "Games/cards/puzzles", "Music", "Community integration", "Other"]],
      ["Activity Narrative", "activityNarrative", "textarea"],
      ["Smoking Plan", "smokingPlan", "textarea"],
      ["Case Management Contact / When CM Will Be Contacted", "caseManagement", "textarea"],
      ["Other Issues / Concerns / Problems", "otherConcerns", "textarea"],
    ],
  },
  {
    title: "Review and Approval",
    fields: [
      ["Date of Original Plan", "originalPlanDate", "date"],
      ["Review / Revision Dates", "revisionDates", "textarea"],
      ["Involved in NCP Development", "involved", "checkboxes", ["Resident", "Resident Representative", "Parent", "Health Professional", "Other"]],
      ["Person Approving Plan", "approvingPerson", "text"],
      ["Resident Verbally Agreed to NCP", "verbalAgreement", "select", ["No", "Yes"]],
      ["NCP Sent to DSHS CM On", "sentToDshs", "date"],
      ["Resident Recommendations", "residentRecommendations", "textarea"],
    ],
  },
];

const isWithinDateRange = (date, startDate, endDate) => {
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

function RecordValue({ record, column }) {
  const [, key, type] = column;
  const value = record[key];

  if (type === "date") {
    return <span>{formatDate(value)}</span>;
  }

  if (type === "files") {
    return (
      <span className="assessment-file-stack">
        {value.map((file) => (
          <small key={file}><i className="ti ti-paperclip" /> {file}</small>
        ))}
      </span>
    );
  }

  if (type === "attachedRecords") {
    return (
      <span className="assessment-record-stack">
        {value.map((record) => (
          <small key={record}>{record}</small>
        ))}
      </span>
    );
  }

  if (type === "status") {
    return <em>{value}</em>;
  }

  if (key === "period" || key === "processName" || key === "title") {
    return <span><strong>{value}</strong>{record.summary ? <small>{record.summary}</small> : null}</span>;
  }

  return <span>{value}</span>;
}

function NcpField({ field }) {
  const [label, name, type, options = []] = field;

  if (type === "textarea") {
    return (
      <label className="ncp-form-field ncp-form-field-wide">
        <span>{label}</span>
        <textarea name={name} rows={3} />
      </label>
    );
  }

  if (type === "select") {
    return (
      <label className="ncp-form-field">
        <span>{label}</span>
        <select name={name}>
          <option value="">Select</option>
          {options.map((option) => (
            <option value={option} key={option}>{option}</option>
          ))}
        </select>
      </label>
    );
  }

  if (type === "checkboxes") {
    return (
      <fieldset className="ncp-form-field ncp-form-field-wide">
        <legend>{label}</legend>
        <div className="ncp-checkbox-grid">
          {options.map((option) => (
            <label key={option}>
              <input type="checkbox" name={`${name}-${option}`} />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <label className="ncp-form-field">
      <span>{label}</span>
      <input name={name} type={type} />
    </label>
  );
}

const assessmentFormSections = [
  {
    title: "Assessment Process",
    fields: [
      ["Process name", "processName", "text"],
      ["Patient type", "patientType", "select", ["NEW", "RESIDENT"]],
      ["Start date", "processStartDate", "date"],
      ["End date", "processEndDate", "date"],
      ["Process status", "processStatus", "select", ["Draft", "Active", "In review", "Confirmed", "Closed"]],
      ["Assessment process owner", "processOwner", "text"],
      ["Attached process records", "attachedProcessRecords", "checkboxes", ["Initial Assessment", "Preliminary Plan", "Notes", "Negotiated Plan", "Final Assessment", "Meet and Greet", "Initial Assessment Review", "Client Onboarding", "Resident 30-Day Probation", "Residency Confirmation"]],
    ],
  },
  {
    title: "Assessment Source",
    fields: [
      ["Assessment type", "assessmentType", "select", ["Initial state assessment", "Annual assessment", "Change-of-condition review", "Final assessment update", "Other"]],
      ["Assessment version", "assessmentVersion", "text"],
      ["Assessment date", "assessmentDate", "date"],
      ["Date received", "dateReceived", "date"],
      ["Social worker", "socialWorker", "text"],
      ["State case ID", "stateCaseId", "text"],
    ],
  },
  {
    title: "Resident and Care Snapshot",
    fields: [
      ["Resident name", "residentName", "text"],
      ["Medication behaviors", "medicationBehaviors", "textarea"],
      ["Diagnosis and history", "diagnosisHistory", "textarea"],
      ["Care needed", "careNeeded", "textarea"],
      ["Medication information", "medicationInformation", "textarea"],
      ["Client information notes", "clientInformation", "textarea"],
    ],
  },
  {
    title: "Attachments and Review",
    fields: [
      ["Assessment file", "assessmentFile", "file"],
      ["Medication profile file", "medicationProfileFile", "file"],
      ["Supporting files", "supportingFiles", "file"],
      ["Imported from state assessment", "importedFromState", "select", ["Yes", "No"]],
      ["Reviewed by", "reviewedBy", "text"],
      ["Review status", "reviewStatus", "select", ["Draft", "Ready for provider review", "Reviewed", "Needs follow-up"]],
      ["Provider review notes", "providerReviewNotes", "textarea"],
    ],
  },
];

function AssessmentDrawerForm() {
  return (
    <form className="assessment-drawer-form">
      {assessmentFormSections.map((section) => (
        <section className="assessment-drawer-section" key={section.title}>
          <h3>{section.title}</h3>
          <div className="assessment-drawer-grid">
            {section.fields.map((field) => (
              <NcpField field={field} key={field[1]} />
            ))}
          </div>
        </section>
      ))}
      <div className="assessment-drawer-actions">
        <button className="btn secondary" type="button">Save draft</button>
        <button className="btn primary" type="button">Add assessment</button>
      </div>
    </form>
  );
}

function ResidentTabList({ title, intro, tab, onCreate }) {
  const config = tabItems[tab];
  const [noteDateRange, setNoteDateRange] = useState({ start: yesterdayDate, end: todayDate });
  const [carePlanMode, setCarePlanMode] = useState(null);
  const isDailyNotesTab = tab === "daily-notes";
  const isNegotiatedCarePlanTab = tab === "negotiated-care-plan";
  const rows = sortedRecordRows(config.rows).filter((record) =>
    !isDailyNotesTab || isWithinDateRange(record.dateAdded, noteDateRange.start, noteDateRange.end)
  );
  const currentRecord = config.current && (!isDailyNotesTab || isWithinDateRange(config.current.dateAdded, noteDateRange.start, noteDateRange.end))
    ? config.current
    : null;
  const openCreate = () => {
    if (isNegotiatedCarePlanTab) {
      setCarePlanMode("new");
      return;
    }
    onCreate(config.createDrawerKey || config.drawerKey);
  };
  const openCurrent = () => {
    if (isNegotiatedCarePlanTab) {
      setCarePlanMode("current");
      return;
    }
    onCreate(config.drawerKey);
  };

  if (isNegotiatedCarePlanTab && carePlanMode) {
    const plan = carePlanMode === "current" ? currentRecord : {
      title: "New negotiated care plan",
      summary: "Create a negotiated care plan from resident notes, provider inputs, and social worker negotiation.",
      dateAdded: new Date().toISOString(),
      socialWorker: "Amanda Parker",
      status: "Draft",
    };

    return (
      <Panel
        title={carePlanMode === "current" ? "Current Negotiated Care Plan" : "Create Negotiated Care Plan"}
        actionNode={(
          <button className="mini-btn previous-plans-button" type="button" onClick={() => setCarePlanMode(null)}>
            <i className="ti ti-arrow-left" /> Previous Plans
          </button>
        )}
      >
        <div className="resident-plan-workspace-head">
          <span>{recordBadge(plan)}</span>
        </div>
        <div className="resident-plan-workspace">
          <div>
            <label>Plan title</label>
            <strong>{recordTitle(plan)}</strong>
          </div>
          <div>
            <label>Social worker</label>
            <strong>{plan.socialWorker}</strong>
          </div>
          <div>
            <label>Date created</label>
            <strong>{formatDate(plan.dateAdded)}</strong>
          </div>
          <div className="resident-plan-workspace-summary">
            <label>Care plan notes</label>
            <p>{plan.summary}</p>
          </div>
        </div>
        <section className="ncp-source-section">
          <div>
            <h3>Data Sources</h3>
            <p>Records used to generate or support this negotiated care plan.</p>
          </div>
          <div className="ncp-source-grid">
            {negotiatedPlanSources.map(([type, title, date, status, icon]) => (
              <article className="ncp-source-card" key={type}>
                <span><i className={`ti ${icon}`} /></span>
                <div>
                  <strong>{type}</strong>
                  <p>{title}</p>
                  <small>{date}</small>
                </div>
                <em>{status}</em>
              </article>
            ))}
          </div>
        </section>
        <form className="ncp-template-form">
          {ncpFormSections.map((section) => (
            <section className="ncp-form-section" key={section.title}>
              <h3>{section.title}</h3>
              <div className="ncp-form-grid">
                {section.fields.map((field) => (
                  <NcpField field={field} key={field[1]} />
                ))}
              </div>
            </section>
          ))}
        </form>
        <div className="resident-plan-workspace-actions">
          <button className="btn primary" type="button">Save draft</button>
          <button className="btn secondary" type="button">Send for review</button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={title}>
      <div className="resident-tab-list-head">
        <p>{intro}</p>
        <button className="mini-btn" type="button" onClick={openCreate}>
          <i className="ti ti-plus" /> {config.createLabel}
        </button>
      </div>
      {isDailyNotesTab ? (
        <div className="resident-date-range">
          <label>
            <span>From</span>
            <input type="date" value={noteDateRange.start} max={noteDateRange.end} onChange={(event) => setNoteDateRange((current) => ({ ...current, start: event.target.value }))} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={noteDateRange.end} min={noteDateRange.start} onChange={(event) => setNoteDateRange((current) => ({ ...current, end: event.target.value }))} />
          </label>
        </div>
      ) : null}
      {currentRecord ? (
        <button className="resident-active-item" type="button" onClick={openCurrent}>
          <span>
            <small>{config.currentLabel}</small>
            <strong>{recordTitle(currentRecord)}</strong>
            <em>{recordSummary(currentRecord)}</em>
          </span>
          <b>{formatDate(currentRecord.dateAdded)}</b>
          <i>{recordBadge(currentRecord)}</i>
        </button>
      ) : null}
      <div className="resident-record-section-title">Previous records</div>
      <div className="resident-tab-list" style={{ "--resident-record-columns": config.gridTemplate }}>
        <div className="resident-tab-list-row resident-tab-list-head-row">
          {config.columns.map(([label]) => (
            <span key={label}>{label}</span>
          ))}
          <span />
        </div>
        {rows.map((record) => (
          <button className="resident-tab-list-row" type="button" onClick={() => onCreate(config.drawerKey)} key={`${tab}-${recordTitle(record)}-${record.dateAdded}`}>
            {config.columns.map((column) => (
              <RecordValue record={record} column={column} key={column[1]} />
            ))}
            <i className="ti ti-chevron-right" />
          </button>
        ))}
      </div>
    </Panel>
  );
}

function OnboardingDataTab({ title, intro, sections = [], error = "" }) {
  const visibleSections = sections.filter((section) => section.rows?.length);
  return (
    <Panel title={title}>
      <div className="resident-onboarding-data-head">
        <p>{intro}</p>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {visibleSections.length ? (
        <div className="resident-onboarding-data-stack">
          {visibleSections.map((section) => (
            <article className="resident-onboarding-data-card" key={section.title}>
              <div className="resident-onboarding-data-card-head">
                <span><i className={`ti ${section.icon || "ti-clipboard-list"}`} /></span>
                <strong>{section.title}</strong>
              </div>
              <dl>
                {section.rows.map(([label, value]) => {
                  const renderedValue = onboardingValue(value);
                  return (
                    <div className={hasHtml(renderedValue) ? "is-rich" : ""} key={label}>
                      <dt>{label}</dt>
                      <dd className={renderedValue ? "" : "is-empty"}>
                        {renderedValue && hasHtml(renderedValue) ? (
                          <span dangerouslySetInnerHTML={{ __html: renderedValue }} />
                        ) : (
                          renderedValue || "Not added"
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </article>
          ))}
        </div>
      ) : (
        <div className="resident-inline-empty">No onboarding data has been captured for this step yet.</div>
      )}
    </Panel>
  );
}

export default function ResidentDetailPage() {
  const { residentId, id: legacyResidentId } = useParams();
  const id = residentId || legacyResidentId;
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [activeTab, setActiveTab] = useState(() => window.location.hash.replace("#", "") || "overview");
  const [resident, setResident] = useState(null);
  const [residentLoading, setResidentLoading] = useState(true);
  const [residentError, setResidentError] = useState("");
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [onboardingPreview, setOnboardingPreview] = useState(null);
  const [onboardingPreviewError, setOnboardingPreviewError] = useState("");
  const residentName = fullNameFor(resident || {});
  const residentInitials = resident?.initials || initialsFor(residentName);
  const residentStatus = labelize(resident?.status || resident?.ncpStatus || resident?.carePlanStatus || "In Review");
  const residentLocation = locationFor(resident || {});
  const residentContact = contactFor(resident || {});
  const residentAge = resident?.age || ageFromBirthDate(resident?.birthDate || resident?.dateOfBirth);
  const residentRoom = resident?.roomNumber || resident?.room || "";
  const residentNextReview = compactDate(resident?.nextReviewDate || resident?.nextReview);
  const residentAdmissionDate = compactDate(resident?.moveInDate || resident?.expectedAdmissionDate || resident?.admissionDate || resident?.movedInDate);
  const residentAddress = addressSummaryFor(resident || {});
  const residentPrimaryContactName = resident?.primaryContactName || resident?.emergencyContactName || residentContact.contactPersonName || "";
  const residentPrimaryContactRelationship = resident?.primaryContactRelationship || resident?.emergencyContactRelationship || residentContact.contactPersonTitle || "";
  const residentPrimaryContactPhone = primaryPhoneFor(resident || {});
  const residentPrimaryContactEmail = primaryEmailFor(resident || {});
  const residentFlowSource = resident?.flowType || resident?.residentFlowType || resident?.patientType || residentFlowType;
  const residentFlow = String(residentFlowSource).toUpperCase() === "NEW" ? "new-resident" : String(residentFlowSource).toUpperCase() === "RESIDENT" ? "existing-resident" : residentFlowSource;
  const currentAssignedStaff = assignedStaff.length ? assignedStaff : [];
  const activeStaff = currentAssignedStaff.find((staff) => String(staff.key || staff.id) === String(activeDrawer));
  const residentPersonaDrawer = {
    title: "Resident Persona",
    intro: "Resident bio data, contact information, next of kin details, and care profile from the resident record.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", displayValue(residentName)],
          ["Preferred name", displayValue(resident?.preferredName)],
          ["Date of birth", displayValue(compactDate(resident?.birthDate || resident?.dateOfBirth))],
          ["Age", displayValue(residentAge)],
          ["Gender", displayValue(labelize(resident?.gender || resident?.sex))],
          ["Primary language", displayValue(resident?.primaryLanguage)],
          ["Room", displayValue(residentRoom)],
          ["Status", displayValue(residentStatus)],
          ["Admission date", displayValue(residentAdmissionDate)],
          ["Discharge date", displayValue(compactDate(resident?.dischargeDate))],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", displayValue(residentPrimaryContactPhone || residentContact.primaryPhone)],
          ["Secondary phone", displayValue(residentContact.secondaryPhone)],
          ["Email", displayValue(residentPrimaryContactEmail || residentContact.primaryEmail)],
          ["Secondary email", displayValue(residentContact.secondaryEmail)],
          ["Address", displayValue(residentAddress)],
          ["City", displayValue(residentLocation.city)],
          ["County", displayValue(residentLocation.county)],
          ["State", displayValue(residentLocation.state)],
          ["Zip Code", displayValue(residentLocation.zipCode || residentLocation.zip)],
        ],
      },
      {
        title: "Primary Contact / Next of Kin",
        rows: [
          ["Name", displayValue(residentPrimaryContactName)],
          ["Relationship", displayValue(residentPrimaryContactRelationship)],
          ["Phone", displayValue(residentPrimaryContactPhone)],
          ["Email", displayValue(residentPrimaryContactEmail)],
          ["PoA / guardian notes", displayValue(resident?.poaGuardianNotes)],
        ],
      },
      {
        title: "Care Profile",
        rows: [
          ["Care level", displayValue(resident?.careLevel)],
          ["NCP status", displayValue(resident?.ncpStatus)],
          ["Next review", displayValue(residentNextReview)],
          ["Primary provider", displayValue(resident?.primaryProvider)],
          ["Pharmacy", displayValue(resident?.pharmacy)],
          ["Diagnosis", displayValue(resident?.diagnosis)],
          ["Medication notes", displayValue(resident?.medicationNotes)],
          ["Care needed", displayValue(resident?.careNeeded)],
          ["Behavior and safety notes", displayValue(resident?.behaviorSafetyNotes)],
          ["General notes", displayValue(resident?.notes)],
        ],
      },
    ],
    action: "Edit persona",
  };
  const drawer = activeStaff
    ? {
        title: activeStaff.name || activeStaff.staffName || "Assigned staff",
        intro: `${activeStaff.role || activeStaff.assignmentRole || "Care provider staff"} assigned to ${residentName} today.`,
        sections: [
          {
            title: "Staff Details",
            rows: [
              ["Name", activeStaff.name || activeStaff.staffName || "Not set"],
              ["Role", activeStaff.role || activeStaff.assignmentRole || "Not set"],
              ["Shift", activeStaff.shift || [activeStaff.from, activeStaff.to].filter(Boolean).join(" - ") || "Not set"],
              ["Assignment", activeStaff.assignment || activeStaff.description || "Not set"],
            ],
          },
          {
            title: "Contact Information",
            rows: [
              ["Phone", activeStaff.phone || "Not set"],
              ["Email", activeStaff.email || "Not set"],
            ],
          },
        ],
        contactActions: [
          ["Chat", "ti-message-circle", "#chat"],
          ["Call", "ti-phone", activeStaff.phone ? `tel:${String(activeStaff.phone).replace(/[^+\d]/g, "")}` : "#"],
        ],
      }
    : activeDrawer
      ? activeDrawer === "persona" ? residentPersonaDrawer : drawerContent[activeDrawer]
      : null;
  const snapshot = [
    ["Admission", residentAdmissionDate || "Not set"],
    ["Primary contact", residentPrimaryContactName || "Not set"],
    ["Relationship", residentPrimaryContactRelationship || "Not set"],
    ["Care level", resident?.careLevel || "Not set"],
    ["Diagnosis", resident?.diagnosis || "Not set"],
    ["Medication support", resident?.medicationNotes || "Not set"],
  ];
  const residentActionSections = residentSections.map(([key, icon, title, text, status, createdAt]) => {
    if (key === "notes") {
      return [key, icon, title, resident?.lastNoteSummary || text, resident?.lastNoteAt ? `Last note ${compactDate(resident.lastNoteAt)}` : status, resident?.lastNoteAt || createdAt];
    }
    if (key === "carePlan") {
      return [key, icon, title, resident?.careNeeded || text, resident?.ncpStatus || status, resident?.updatedAt || createdAt];
    }
    if (key === "documents") {
      return [key, icon, title, resident?.stateAssessmentFileName ? `State assessment: ${resident.stateAssessmentFileName}` : text, resident?.stateAssessmentImported ? "Imported" : status, resident?.updatedAt || createdAt];
    }
    if (key === "alerts") {
      return [key, icon, title, text, `${Number(resident?.openAlertCount || 0)} open`, resident?.updatedAt || createdAt];
    }
    if (key === "assessments") {
      return [key, icon, title, resident?.intakeSource ? `Intake source: ${resident.intakeSource}` : text, resident?.stateAssessmentImported ? "Assessment imported" : status, resident?.updatedAt || createdAt];
    }
    if (key === "team") {
      return [key, icon, title, resident?.careTeamSummary || text, currentAssignedStaff.length ? `${currentAssignedStaff.length} assigned today` : status, resident?.updatedAt || createdAt];
    }
    return [key, icon, title, text, status, createdAt];
  });
  const onboardingRoot = onboardingPreview?.previewPayload || onboardingPreview?.payload || onboardingPreview || {};
  const onboardingStages =
    onboardingRoot.onboardingStages ||
    onboardingRoot.previewPayload?.onboardingStages ||
    onboardingRoot.payload?.onboardingStages ||
    {};
  const bioStage = onboardingStages.bio || onboardingRoot.bio || {};
  const contactsStage = onboardingStages.contacts || onboardingRoot.contacts || {};
  const careHistoryStage = onboardingStages.careHistory || onboardingRoot.careHistory || {};
  const careSnapshotStage = onboardingStages.careSnapshot || onboardingStages.care || onboardingRoot.careSnapshot || onboardingRoot.care || {};
  const documentsStage = onboardingStages.documents || onboardingRoot.documents || {};
  const onboardingBio = bioStage.resident || bioStage.payload?.resident || bioStage.payload || bioStage || {};
  const onboardingBioSourceForm = onboardingBio.sourceForm || {};
  const onboardingBioFormData = onboardingBioSourceForm.formData || {};
  const onboardingContacts = contactsStage.contacts || contactsStage.payload?.contacts || onboardingBio.contacts || [];
  const onboardingCareHistory = careHistoryStage.careHistory || careHistoryStage.payload?.careHistory || onboardingBio.careHistory || {};
  const onboardingCareSnapshot = careSnapshotStage.initialCareSnapshot || careSnapshotStage.payload?.initialCareSnapshot || onboardingBio.initialCareSnapshot || {};
  const onboardingDocuments = documentsStage.documents || documentsStage.payload?.documents || onboardingBio.documents || {};
  const bioPersonal = onboardingBio.personalInformation || {};
  const bioCoverage = onboardingBio.healthAndCoverage || {};
  const onboardingBioSections = [
    {
      title: "Personal Information",
      icon: "ti-user-circle",
      rows: [
        ["Full name", [bioPersonal.firstName || onboardingBioFormData.firstName || resident?.firstName, bioPersonal.middleName || onboardingBioFormData.middleName || resident?.middleName, bioPersonal.lastName || onboardingBioFormData.lastName || resident?.lastName].filter(Boolean).join(" ") || residentName],
        ["Preferred name", bioPersonal.preferredName || onboardingBioFormData.preferredName || resident?.preferredName],
        ["Birth date", compactDate(bioPersonal.birthDate || onboardingBioFormData.dateOfBirth || resident?.birthDate)],
        ["Sex", labelize(bioPersonal.sex || onboardingBioFormData.sex || resident?.gender)],
        ["Social Security Number", bioPersonal.socialSecurityNumber || onboardingBioFormData.socialSecurityNumber],
        ["Primary language", bioPersonal.primaryLanguage || onboardingBioFormData.primaryLanguage || resident?.primaryLanguage],
      ],
    },
    {
      title: "Address",
      icon: "ti-map-pin",
      rows: [
        ["Address", addressSummary(onboardingBio.address) || residentAddress],
        ["City", onboardingBio.address?.city || onboardingBioFormData.city || residentLocation.city],
        ["County", onboardingBio.address?.county || onboardingBioFormData.county || residentLocation.county],
        ["State", onboardingBio.address?.state || onboardingBioFormData.state || residentLocation.state],
        ["Country", onboardingBio.address?.country || onboardingBioFormData.country || residentLocation.country],
        ["Zip Code", onboardingBio.address?.zipCode || onboardingBio.address?.zip || onboardingBioFormData.zip || residentLocation.zipCode],
      ],
    },
    {
      title: "Coverage and Notes",
      icon: "ti-shield-check",
      rows: [
        ["Code status", bioCoverage.codeStatus || onboardingBioFormData.codeStatus],
        ["Advanced directives", bioCoverage.advancedDirectives ?? onboardingBioFormData.advancedDirectives],
        ["Medicare Number", bioCoverage.medicareNumber || onboardingBioFormData.medicareNumber],
        ["Medicaid Number", bioCoverage.medicaidNumber || onboardingBioFormData.medicaidNumber],
        ["Supplementary insurances", bioCoverage.supplementaryInsurances || onboardingBioSourceForm.otherInsurances],
        ["Background notes", onboardingBio.backgroundNotes || onboardingBio.notes || onboardingBioFormData.bioNotes || resident?.notes],
      ],
    },
  ];
  const fallbackContacts = residentPrimaryContactName ? [{
    relationship: residentPrimaryContactRelationship || "Primary contact",
    firstName: residentPrimaryContactName,
    email: residentPrimaryContactEmail,
    phones: { preferred: residentPrimaryContactPhone },
    primaryContact: true,
  }] : [];
  const onboardingContactSections = (onboardingContacts.length ? onboardingContacts : fallbackContacts).map((contact, index) => ({
    title: `${contact.relationship || contact.contactType || "Contact"} ${index + 1}`,
    icon: "ti-address-book",
    rows: [
      ["Name", [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") || contact.organizationName],
      ["Relationship", contact.relationship],
      ["Contact type", labelize(contact.contactType)],
      ["Email", contact.email],
      ["Phones", phonesSummary(contact.phones)],
      ["Address", addressSummary(contact.address)],
      ["Primary contact", contact.primaryContact],
      ["Emergency contact", contact.emergencyContact],
      ["Decision maker", contact.decisionMaker],
      ["Next of kin", contact.nextOfKin],
      ["Notes", contact.notes],
    ],
  }));
  const onboardingCareHistorySections = [
    {
      title: "Admission Source",
      icon: "ti-building-hospital",
      rows: [
        ["Admitted at hospital", onboardingCareHistory.admittedAtHospital],
        ["Hospital", onboardingCareHistory.hospitalAdmission?.name || onboardingBioFormData.hospitalName],
        ["Hospital contact", onboardingCareHistory.hospitalAdmission?.contactName || onboardingBioFormData.hospitalContactName],
        ["Hospital phone", phonesSummary(onboardingCareHistory.hospitalAdmission?.phones) || onboardingBioFormData.hospitalPhone],
        ["Hospital email", onboardingCareHistory.hospitalAdmission?.email || onboardingBioFormData.hospitalEmail],
        ["Hospital address", addressSummary(onboardingCareHistory.hospitalAdmission?.address) || addressSummary(locationFor({ locationInformation: {
          addressLine1: onboardingBioFormData.hospitalAddress,
          city: onboardingBioFormData.hospitalCity,
          county: onboardingBioFormData.hospitalCounty,
          state: onboardingBioFormData.hospitalState,
          country: onboardingBioFormData.hospitalCountry,
          zipCode: onboardingBioFormData.hospitalZip,
        } }))],
        ["Hospital notes", onboardingCareHistory.hospitalAdmission?.notes || onboardingBioFormData.admissionDescription],
      ],
    },
    {
      title: "Previous Family Home",
      icon: "ti-home-heart",
      rows: [
        ["Coming from another family home", onboardingCareHistory.comingFromAnotherFamilyHome],
        ["Family home name", onboardingCareHistory.previousFamilyHome?.name],
        ["Contact name", onboardingCareHistory.previousFamilyHome?.contactName],
        ["Phone", phonesSummary(onboardingCareHistory.previousFamilyHome?.phones)],
        ["Email", onboardingCareHistory.previousFamilyHome?.email],
        ["Address", addressSummary(onboardingCareHistory.previousFamilyHome?.address)],
        ["Length of stay", [onboardingCareHistory.previousFamilyHome?.years ? `${onboardingCareHistory.previousFamilyHome.years} years` : "", onboardingCareHistory.previousFamilyHome?.months ? `${onboardingCareHistory.previousFamilyHome.months} months` : ""].filter(Boolean).join(", ")],
        ["Notes", onboardingCareHistory.previousFamilyHome?.notes],
      ],
    },
    {
      title: "Prior Caregiver",
      icon: "ti-user-heart",
      rows: [
        ["Had prior caregiver", onboardingCareHistory.hadPriorCaregiver],
        ["Caregiver name", [onboardingCareHistory.priorCaregiver?.firstName, onboardingCareHistory.priorCaregiver?.middleName, onboardingCareHistory.priorCaregiver?.lastName].filter(Boolean).join(" ")],
        ["Relationship", onboardingCareHistory.priorCaregiver?.relationship],
        ["Phone", phonesSummary(onboardingCareHistory.priorCaregiver?.phones)],
        ["Email", onboardingCareHistory.priorCaregiver?.email],
        ["Address", addressSummary(onboardingCareHistory.priorCaregiver?.address)],
        ["Care details", onboardingCareHistory.priorCaregiver?.notes],
      ],
    },
    {
      title: "Care History Notes",
      icon: "ti-notes",
      rows: [["Notes", onboardingCareHistory.notes]],
    },
  ];
  const onboardingCareSnapshotSections = [
    {
      title: "Initial Care Snapshot",
      icon: "ti-heart-rate-monitor",
      rows: [
        ["Condition summary", onboardingCareSnapshot.conditionSummary || onboardingBioFormData.admissionDescription],
        ["Diagnosis", onboardingCareSnapshot.diagnosis || onboardingBioFormData.diagnosis || resident?.diagnosis],
        ["Allergies", onboardingCareSnapshot.allergies || onboardingBioFormData.allergies],
        ["Medication notes", onboardingCareSnapshot.medicationNotes || onboardingBioFormData.medicationNotes || resident?.medicationNotes],
        ["Care needed", onboardingCareSnapshot.careNeeded || onboardingBioFormData.careNeeded || resident?.careNeeded],
        ["Behavior and safety notes", onboardingCareSnapshot.behaviorSafetyNotes || onboardingBioFormData.behaviorSafetyNotes || resident?.behaviorSafetyNotes],
      ],
    },
    {
      title: "Custom Questions",
      icon: "ti-question-mark",
      rows: (onboardingCareSnapshot.customQuestions || []).map((question, index) => [
        question.question || `Question ${index + 1}`,
        question.answer || question.response,
      ]),
    },
  ];
  const onboardingDocumentSections = [
    {
      title: "Uploaded Documents",
      icon: "ti-file-upload",
      rows: (onboardingDocuments.uploadedDocuments || onboardingPreview?.uploadedDocuments || (resident?.stateAssessmentFileName ? [{ name: resident.stateAssessmentFileName, fileName: resident.stateAssessmentFileName, description: "State assessment file" }] : [])).map((document, index) => [
        document.name || document.fileName || `Document ${index + 1}`,
        [document.description, document.fileName, document.confirmed ? "Confirmed" : "", document.storageStatus].filter(Boolean).join(" | "),
      ]),
    },
    {
      title: "Signed Document Validation",
      icon: "ti-signature",
      rows: (onboardingDocuments.signedDocumentValidations || []).map((document) => [
        document.documentName || "Signed document",
        `Signed verified: ${document.signedVerified ? "Yes" : "No"} | File uploaded: ${document.fileUploaded ? "Yes" : "No"}${document.notes ? ` | Notes: ${document.notes}` : ""}`,
      ]),
    },
  ];

  const openDrawer = (key) => setActiveDrawer(key);
  const closeDrawer = () => setActiveDrawer(null);
  const showTab = (tabId) => activeTab === tabId;
  const selectTab = (tabId) => {
    setActiveTab(tabId);
    window.history.replaceState(null, "", `#${tabId}`);
  };

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash.replace("#", "") || "overview");
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (!id) {
      setResidentLoading(false);
      setResidentError("Resident id is missing from the page URL.");
      return undefined;
    }
    let isMounted = true;
    setResidentLoading(true);
    Promise.all([
      careService.residents.get(id),
      careService.careStaffAssignments.list({ residentId: id, date: new Date().toISOString().slice(0, 10) }),
      careService.residents.getOnboardingPreview(id)
        .then((data) => ({ data }))
        .catch((error) => ({ error })),
    ])
      .then(([residentData, staffData, onboardingResult]) => {
        if (!isMounted) return;
        setResident(residentData || null);
        setAssignedStaff(Array.isArray(staffData) ? staffData : []);
        if (onboardingResult?.error) {
          setOnboardingPreview(null);
          setOnboardingPreviewError(onboardingResult.error?.response?.data?.message || onboardingResult.error?.message || "Unable to load onboarding details.");
        } else {
          setOnboardingPreview(onboardingResult?.data || null);
          setOnboardingPreviewError("");
        }
        setResidentError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setResident(null);
        setAssignedStaff([]);
        setOnboardingPreview(null);
        setOnboardingPreviewError("");
        setResidentError(err?.response?.data?.message || err?.message || "Unable to load resident details.");
      })
      .finally(() => {
        if (isMounted) setResidentLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id]);

  return (
    <CareShell className="dashboard-content resident-content">
      <div className="crumbs">Residents <i className="ti ti-chevron-right" /> {residentName}</div>
      {residentError ? <p className="form-error">{residentError}</p> : null}
      <div className="resident-hero">
        <div
          className="resident-photo"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(231, 246, 239, 0.72), rgba(231, 248, 231, 0.88)), url("${publicAsset("/welcome3.png")}")`,
          }}
        >
          {residentLoading ? "..." : residentInitials}
        </div>
        <div className="resident-hero-title">
          <h1>{residentLoading ? "Loading resident..." : residentName}</h1>
          <div className="resident-hero-title-actions">
            <em>{residentStatus}</em>
          </div>
        </div>
        <dl>
          <div><i className="ti ti-door" /><dt>Room</dt><dd>{residentRoom || "Not set"}</dd></div>
          <div><i className="ti ti-user" /><dt>Age</dt><dd>{residentAge || "Not set"}</dd></div>
          <div><i className="ti ti-calendar" /><dt>Next Review</dt><dd>{residentNextReview || "Not set"}</dd></div>
          <div className="resident-assigned-staff">
            <i className="ti ti-users-group" />
            <dt>Assigned Today</dt>
            <dd>
              {currentAssignedStaff.length ? currentAssignedStaff.map((staff) => (
                <button className="resident-staff-link" type="button" onClick={() => openDrawer(staff.key || staff.id)} key={staff.key || staff.id}>{staff.name || staff.staffName}</button>
              )) : <span>Not assigned</span>}
            </dd>
          </div>
        </dl>
      </div>
      <section className="resident-flow-stage-strip" aria-label="Resident care plan flow stage">
        <div className="resident-flow-stage-head">
          <span>Current Flow</span>
          <strong>{residentFlow === "new-resident" ? "New Resident Onboarding" : "Existing Resident Review"}</strong>
        </div>
        <div className="resident-flow-stage-list">
          {carePlanFlowStages
            .filter((stage) => stage.appliesTo.includes(residentFlow))
            .map((stage) => (
              <article className={`resident-flow-stage is-${stage.status.toLowerCase().replace(/\s+/g, "-")}`} key={stage.label}>
                <span><i className={`ti ${stage.icon}`} /></span>
                <div>
                  <strong>{stage.label}</strong>
                  <small>{stage.text}</small>
                </div>
                <em>{stage.status}</em>
              </article>
            ))}
        </div>
      </section>
      <nav className="tabs">
        {residentTabs.map(([id, tab]) => (
          <button className={id === activeTab ? "active" : ""} type="button" onClick={() => selectTab(id)} key={id}>{tab}</button>
        ))}
      </nav>

      {showTab("overview") && <section className="resident-bookmark-section" id="overview">
        <div className="resident-detail-grid">
          <Panel title="Resident Snapshot">
            <div className="resident-snapshot-grid">
              {snapshot.map(([label, value]) => (
                <div className="resident-snapshot-item" key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Next Actions">
            <div className="resident-next-actions">
              <button className="mini-btn action-note" type="button" onClick={() => openDrawer("notes")}><i className="ti ti-note" /> Add daily note</button>
              <button className="mini-btn action-plan" type="button" onClick={() => openDrawer("carePlan")}><i className="ti ti-heart-handshake" /> Open NCP</button>
              <button className="mini-btn action-files" type="button" onClick={() => openDrawer("documents")}><i className="ti ti-file-download" /> View files</button>
              <button className="mini-btn action-persona" type="button" onClick={() => openDrawer("persona")}><i className="ti ti-id" /> View Resident Persona</button>
              {id ? (
                <Link className="mini-btn action-onboarding" to={`/residents/${id}/onboarding`}>
                  <i className="ti ti-clipboard-list" /> Onboarding Details
                </Link>
              ) : null}
            </div>
          </Panel>
        </div>

        <Panel title="Resident Data and Action Points">
          <div className="resident-section-grid">
            {residentActionSections.map(([key, icon, title, text, status, createdAt]) => (
              <button className="resident-section-card" type="button" onClick={() => openDrawer(key)} key={title}>
                <span className="round-icon blue"><i className={`ti ${icon}`} /></span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <small>Created {new Date(createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</small>
                </div>
                <em>{status}</em>
              </button>
            ))}
          </div>
        </Panel>
      </section>}

      {showTab("onboarding-bio") && <section className="resident-bookmark-section" id="onboarding-bio">
        <OnboardingDataTab
          title="Onboarding Bio Data"
          intro="Personal bio data, address, insurance, and coverage details captured during resident onboarding."
          sections={onboardingBioSections}
          error={onboardingPreviewError}
        />
      </section>}

      {showTab("onboarding-contacts") && <section className="resident-bookmark-section" id="onboarding-contacts">
        <OnboardingDataTab
          title="Onboarding Contacts"
          intro="Family, guardians, PoA, emergency contacts, and care-circle contacts captured during onboarding."
          sections={onboardingContactSections}
          error={onboardingPreviewError}
        />
      </section>}

      {showTab("onboarding-care-history") && <section className="resident-bookmark-section" id="onboarding-care-history">
        <OnboardingDataTab
          title="Onboarding Care History"
          intro="Hospital admission, prior family home, prior caregiver, and care history details captured during onboarding."
          sections={onboardingCareHistorySections}
          error={onboardingPreviewError}
        />
      </section>}

      {showTab("onboarding-care-snapshot") && <section className="resident-bookmark-section" id="onboarding-care-snapshot">
        <OnboardingDataTab
          title="Onboarding Care Snapshot"
          intro="Initial condition, diagnosis, allergies, medications, care needs, behavior, and custom care questions."
          sections={onboardingCareSnapshotSections}
          error={onboardingPreviewError}
        />
      </section>}

      {showTab("onboarding-documents") && <section className="resident-bookmark-section" id="onboarding-documents">
        <OnboardingDataTab
          title="Onboarding Documents"
          intro="Uploaded files and signed-document validation captured during onboarding."
          sections={onboardingDocumentSections}
          error={onboardingPreviewError}
        />
      </section>}

      {showTab("assessment") && <section className="resident-bookmark-section" id="assessment">
        <ResidentTabList title="Assessments" intro="State assessments, MaG evaluations, annual reviews, and final assessment updates for this resident." tab="assessment" onCreate={openDrawer} />
      </section>}

      {showTab("preliminary-plan") && <section className="resident-bookmark-section" id="preliminary-plan">
        <ResidentTabList title="Preliminary Plans" intro="Provider-created preliminary plans based on admission paperwork, state assessment, MaG, and family discovery." tab="preliminary-plan" onCreate={openDrawer} />
      </section>}

      {showTab("negotiated-care-plan") && <section className="resident-bookmark-section" id="negotiated-care-plan">
        <ResidentTabList title="Negotiated Care Plans" intro="Negotiated care plan history, 30-day observation outputs, final provider inputs, pay negotiation, and state confirmations." tab="negotiated-care-plan" onCreate={openDrawer} />
      </section>}

      {showTab("daily-notes") && <section className="resident-bookmark-section" id="daily-notes">
        <ResidentTabList title="Daily Notes" intro="Daily notes capture behavior, health, welfare, discrepancies, and care changes as they happen." tab="daily-notes" onCreate={openDrawer} />
      </section>}

      {showTab("documents") && <section className="resident-bookmark-section" id="documents">
        <ResidentTabList title="Documents" intro="Signed agreements, policies, PoA documentation, state assessment files, and confirmed care plan files." tab="documents" onCreate={openDrawer} />
      </section>}

      {showTab("care-team") && <section className="resident-bookmark-section" id="care-team">
        <ResidentTabList title="Care Teams" intro="Coordinate updates with PCP, physical therapy, occupational therapy, family or PoA, social worker, and internal staff." tab="care-team" onCreate={openDrawer} />
      </section>}
      <Drawer
        title={drawer?.title}
        placement="right"
        open={Boolean(drawer)}
        onClose={closeDrawer}
        width={520}
        className="resident-action-drawer"
      >
        {drawer ? (
          <div className="resident-drawer-content">
            <p>{drawer.intro}</p>
            {drawer.form === "assessment" ? (
              <AssessmentDrawerForm />
            ) : drawer.sections ? (
              <div className="resident-drawer-list persona-drawer-list">
                {drawer.sections.map((section) => (
                  <article key={section.title}>
                    <strong>{section.title}</strong>
                    <dl>
                      {section.rows.map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </article>
                ))}
              </div>
            ) : (
              <div className="resident-drawer-list">
                {drawer.rows.map(([label, text]) => (
                  <article key={label}>
                    <strong>{label}</strong>
                    <span>{text}</span>
                  </article>
                ))}
              </div>
            )}
            {drawer.contactActions ? (
              <div className="resident-contact-actions">
                {drawer.contactActions.map(([label, icon, href]) => (
                  <a className="mini-btn" href={href} key={label}><i className={`ti ${icon}`} /> {label}</a>
                ))}
              </div>
            ) : null}
            {!drawer.form ? (
              <button className="btn primary" type="button">{drawer.action}</button>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
